import { useState } from 'react';
import { Box, Card, CardContent, Typography, TextField, Button, Alert } from '@mui/material';
import { User as UserIcon, Save, X } from 'lucide-react';
import { useCurrentUser } from '@diggens/user-management';
import { useUpdateUser } from '@diggens/shared-hooks';

interface UserProfileProps {
  userId?: string; // If provided, shows that user's profile (admin view)
  readOnly?: boolean;
}

export function UserProfile({ userId, readOnly = false }: UserProfileProps) {
  const { data: currentUser } = useCurrentUser();
  const user = userId ? undefined : currentUser; // TODO: Add useUser hook for viewing other users

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    organization: user?.organization || '',
  });

  const updateMutation = useUpdateUser();

  const handleFieldChange = (field: keyof typeof formData, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSave = () => {
    updateMutation.mutate(
      { userId: user!.id, updates: formData },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      }
    );
  };

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">No user information available.</Typography>
      </Box>
    );
  }

  const handleCancel = () => {
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      organization: user.organization || '',
    });
    setIsEditing(false);
  };

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <UserIcon size={24} strokeWidth={2} />
            <Typography variant="h6">User Information</Typography>
          </Box>

          {updateMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to update profile. Please try again.
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                First Name
              </Typography>
              <TextField
                fullWidth
                value={isEditing ? formData.first_name : user.first_name || ''}
                onChange={(e) => handleFieldChange('first_name', e.target.value)}
                disabled={!isEditing}
                size="small"
              />
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Last Name
              </Typography>
              <TextField fullWidth value={isEditing ? formData.last_name : user.last_name || ''} onChange={(e) => handleFieldChange('last_name', e.target.value)} disabled={!isEditing} size="small" />
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Email
              </Typography>
              <TextField fullWidth value={user.email || ''} disabled size="small" />
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Organization
              </Typography>
              <TextField
                fullWidth
                value={isEditing ? formData.organization : user.organization || ''}
                onChange={(e) => handleFieldChange('organization', e.target.value)}
                disabled={!isEditing}
                size="small"
              />
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Role
              </Typography>
              <TextField fullWidth value={user.role || ''} disabled size="small" />
            </Box>
          </Box>

          {!readOnly && (
            <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 2 }}>
              {isEditing ? (
                <>
                  <Button variant="contained" startIcon={<Save size={16} />} onClick={handleSave} disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button variant="outlined" startIcon={<X size={16} />} onClick={handleCancel} disabled={updateMutation.isPending}>
                    Cancel
                  </Button>
                </>
              ) : (
                <Button variant="outlined" onClick={() => setIsEditing(true)}>
                  Edit Profile
                </Button>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
